import { TrendingDown, TrendingFlat, TrendingUp } from "@mui/icons-material";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { t } from "i18next";
import { get, lowerCase } from "lodash";

import "./style.css";

function SecondTable({ parameters, children, slowTraining, dateFormat }) {
  const getFormatUser = () => {
    if (!dateFormat) return "DD/MM/YYYY HH:mm";

    let charMapper = {
      d: "DD",
      m: "MM",
      y: "YYYY",
    };

    let joinChar = "-";

    return String(dateFormat)
      .split("/")
      .map((char) => charMapper[lowerCase(char)])
      .join(joinChar)
      .concat(" HH:mm");
  };

  const getColumns = () => {
    let param = get(parameters, "[0].computed_key_parameters", []);
    let columns = [
      `${t("date")} / ${t("eap:table:headers:track")} /${t("editTrainingForm:trainingType:label")} /${t("eap:table:headers:trackCondition")}`,
    ];
    if (param) {
      let arr = param.map(
        (e) =>
          get(
            e,
            "key_parameter_layout.insight_parameter.short_name_translated",
            get(e, "key_parameter_layout.insight_parameter.name_translated"),
          ) +
          `   (${get(e, "key_parameter_layout.insight_parameter.unit_translated")})`,
      );
      columns.push(...arr);
    }
    return columns;
  };

  const getExamData = (training) => {
    if (training) {
      let data = [];

      data.push(dayjs(get(training, "creation_date")).format(getFormatUser()));
      data.push(
        get(
          training,
          "track_name.display_name",
          get(training, "track_name_automatic.display_name", "-"),
        ),
      );
      data.push(
        get(
          training,
          "training_type.display_name",
          get(training, "training_type_automatic.display_name", "-"),
        ),
      );
      data.push(get(training, "track_condition.display_name", "-"));
      return data.join(" / ");
    } else {
      return "-";
    }
  };

  const getEvolutionIcon = (computedKeyParameter) => {
    if (
      computedKeyParameter["arrow_direction"] != null &&
      computedKeyParameter["arrow_color"] != 0
    ) {
      switch (computedKeyParameter["arrow_direction"]) {
        case "up":
          return (
            <TrendingUp
              htmlColor={computedKeyParameter["arrow_color"]}
              sx={{ fontSize: "0.85rem" }}
            />
          );
        case "down":
          return (
            <TrendingDown
              htmlColor={computedKeyParameter["arrow_color"]}
              sx={{ fontSize: "0.85rem" }}
            />
          );
        case "stable":
          return (
            <TrendingFlat
              htmlColor={computedKeyParameter["arrow_color"]}
              sx={{ fontSize: "0.85rem" }}
            />
          );
        default:
          return (
            <TrendingFlat
              htmlColor={computedKeyParameter["arrow_color"]}
              sx={{ fontSize: "0.85rem" }}
            />
          );
      }
    }
  };

  const compactFontSize = slowTraining ? "0.6rem" : "0.65rem";
  const compactMargin = slowTraining ? 1 : 1.5;
  const compactBarHeight = "5px";
  const compactBarMarginBottom = "2px";
  const compactHighlightPadding = "1px 2px";

  const columns = getColumns();
  const totalCols = columns.length;

  // Calculate flex weights: first col = 1.2, remaining cols = 1 each
  const totalWeight = totalCols > 1 ? 1.2 + (totalCols - 1) * 1 : 1.2;
  const leadColWidth = `${(2 / totalWeight) * 100}%`;
  const dataColWidth = `${(1 / totalWeight) * 100}%`;

  return (
    <>
      {children}
      <TableContainer
        sx={{
          mt: compactMargin,
          border: "2px solid",
          borderColor: "#4a0e0d",
          borderRadius: "6px",
          width: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Table
          size="small"
          sx={{
            width: "100%",
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: 0,  
            "& tr:nth-of-type(odd)": {
              backgroundColor: "#eee",
            },
          }}
        >
          <TableBody>
            <TableRow
              className="bg-gradient"
              sx={{ position: "sticky", top: 0, left: 0 }}
            >
              {columns.map((col, i) => (
                <TableCell
                  component="th"
                  key={i}
                  align="center"
                  sx={{
                    width: i === 0 ? leadColWidth : dataColWidth,
                    position: i === 0 ? "sticky" : "static",
                    left: i === 0 ? 0 : "auto",
                    backgroundColor: i === 0 ? "#4a0e0d" : "transparent",
                    color: "#ffffff",
                    fontWeight: 600,
                    py: "3px",
                    px: "1px",
                    fontSize: compactFontSize,
                    borderRight:
                      i !== columns.length - 1 ? "1px solid #ffffff" : "none",
                    borderBottom: "none",
                    boxSizing: "border-box",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  <Typography
                    id="header_police"
                    component="p"
                    sx={{
                      px: "1px",
                      lineHeight: 1.15,
                      fontSize: "inherit",
                      fontWeight: "inherit",
                      m: 0,
                    }}
                  >
                    {col.replace("→", "\u2192")}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            {parameters.map((param, i) => (
              <TableRow
                key={i}
                sx={{
                  borderBottom:
                    i !== parameters.length - 1 ? "1px solid #4a0e0d" : "none",
                }}
              >
                <TableCell
                  align="center"
                  sx={{
                    width: leadColWidth,
                    fontWeight: 600,
                    position: "sticky",
                    left: 0,
                    p:"2px",
                    borderLeft: "1px solid #ffffff",
                    fontSize: compactFontSize,
                    borderBottom: "none",
                    boxSizing: "border-box",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  <Box
                    id="tr_police"
                    component={'p'}
                    className={i % 2 === 0 ? "" : "stick_bg"}
                    sx={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: i % 2 === 0 ? "#ffffff" : undefined,
                      p:"2px",
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      lineHeight: 1.15,
                    }}
                  >
                    {getExamData(param?.training)}
                  </Box>
                </TableCell>
                {param?.computed_key_parameters?.map((paramKey, index) => (
                  <TableCell
                    id="tr_police"
                    key={index}
                    align="center"
                    sx={{
                      width: dataColWidth,
                      borderLeft: "1px solid #ffffff",
                      fontSize: compactFontSize,
                      p:"2px",
                      borderBottom: "none",
                      boxSizing: "border-box",
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {paramKey.color && (
                      <Box
                        sx={{
                          width: "40%",
                          mx: "auto",
                          height: compactBarHeight,
                          mb: compactBarMarginBottom,
                          borderRadius: "4px",
                          background: paramKey.color,
                        }}
                      />
                    )}
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        lineHeight: 1.15,
                        ...(paramKey.highlighted && {
                          backgroundColor: "#d1d5db",
                          borderRadius: "6px",
                          p: compactHighlightPadding,
                          border: "1px solid #000000",
                        }),
                      }}
                    >
                      {get(paramKey, "formatted_value", "-")}
                    </Box>

                    {/* evolution */}
                    {get(
                      paramKey,
                      "key_parameter_layout.insight_parameter.category",
                    ) === "locomotion" &&
                      get(paramKey, "arrow_direction", null) !== null && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            verticalAlign: "middle",
                            ml: "1px",
                          }}
                        >
                          {getEvolutionIcon(paramKey)}
                        </Box>
                      )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default SecondTable;
